import React, { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import ProductCard from '../ProductCard/ProductCard';
import { products, categories, navCategories, productInCategory, productInSubcategory } from '../../data/products';
import './Category.css';

const formatSubcategoryLabel = (slug) => {
  if (!slug) return '';
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Category = () => {
  const { id, subcategory } = useParams();
  const category = categories.find((c) => c.id === id);
  const navCategory = navCategories.find((c) => c.id === id);
  const subcategoryLabel = formatSubcategoryLabel(subcategory);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, subcategory]);

  const categoryProducts = useMemo(() => {
    return products.filter(
      (p) => productInCategory(p, id) && productInSubcategory(p, subcategory)
    );
  }, [id, subcategory]);

  if (!category) {
    return (
      <div className="category-page">
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <h1>Category Not Found</h1>
          <p style={{ color: '#666', marginBottom: '24px' }}>The category you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/shop" className="category-cta-btn">
            <ArrowLeft size={16} /> Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page">
      <nav className="product-breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight size={14} />
        <Link to={`/category/${id}`}>{category.name}</Link>
        {subcategoryLabel && (
          <>
            <ChevronRight size={14} />
            <span>{subcategoryLabel}</span>
          </>
        )}
      </nav>

      <div className="category-hero">
        <img src={category.image} alt={category.name} className="category-hero-image" />
        <div className="category-hero-overlay">
          <h1 className="category-hero-title">{category.name}</h1>
          <p className="category-hero-count">
            {categoryProducts.length} style{categoryProducts.length === 1 ? '' : 's'}
            {subcategoryLabel ? ` in ${subcategoryLabel}` : ''}
          </p>
        </div>
      </div>

      {navCategory && (
        <div className="category-subcategories">
          <h3 className="category-subcategories-title">Shop by department</h3>
          <div className="category-subcategories-list">
            <Link
              to={`/category/${id}`}
              className={`category-subcategory-link${!subcategory ? ' active' : ''}`}
            >
              All
            </Link>
            {navCategory.subcategories.map((sub) => {
              const slug = sub.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link
                  key={sub}
                  to={`/category/${id}/${slug}`}
                  className={`category-subcategory-link${subcategory === slug ? ' active' : ''}`}
                >
                  {sub}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="category-products">
        <div className="category-products-header">
          <h2 className="category-products-title">
            {subcategoryLabel ? `${subcategoryLabel} — ${category.name}` : `All ${category.name}`}
          </h2>
          <Link to="/shop" className="category-back-link">
            <ArrowLeft size={16} /> Back to All
          </Link>
        </div>

        <div className="category-products-grid">
          {categoryProducts.length > 0 ? (
            categoryProducts.map((product) => <ProductCard key={product.id} product={product} />)
          ) : (
            <div className="category-empty">
              <h3>No products found</h3>
              <p>Try another department or browse the full catalog.</p>
              <Link to={`/category/${id}`} className="category-cta-btn" style={{ marginTop: 16 }}>
                View all {category.name}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;
